/**
 * MetricCard - Displays a single metric with value, change indicator, and optional icon.
 */

interface MetricCardProps {
  title: string;
  value: string | number;
  change?: number;
  changeLabel?: string;
  icon?: React.ReactNode;
  loading?: boolean;
}

export function MetricCard({
  title,
  value,
  change,
  changeLabel,
  icon,
  loading = false,
}: MetricCardProps) {
  const getChangeStyle = (changeValue: number) => {
    if (changeValue > 0) return "text-green-600 bg-green-50";
    if (changeValue < 0) return "text-red-600 bg-red-50";
    return "text-gray-600 bg-gray-50";
  };

  const getChangeIcon = (changeValue: number) => {
    if (changeValue > 0) return "↑";
    if (changeValue < 0) return "↓";
    return "";
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 animate-pulse">
        <div className="flex items-center justify-between">
          <div className="w-10 h-10 rounded-lg bg-gray-200" />
          <div className="w-16 h-5 rounded-full bg-gray-200" />
        </div>
        <div className="mt-4 space-y-2">
          <div className="w-20 h-8 bg-gray-200 rounded" />
          <div className="w-24 h-4 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between">
        {icon && (
          <div className="w-10 h-10 rounded-lg bg-magic-100 text-magic-600 flex items-center justify-center">
            {icon}
          </div>
        )}
        {change !== undefined && (
          <span
            className={`text-xs font-medium px-2 py-1 rounded-full ${getChangeStyle(change)}`}
          >
            {getChangeIcon(change)} {Math.abs(change)}%
            {changeLabel && ` ${changeLabel}`}
          </span>
        )}
      </div>
      <div className="mt-4">
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        <p className="text-sm text-gray-500">{title}</p>
      </div>
    </div>
  );
}

export default MetricCard;
